import React, { useCallback, useState } from 'react'
import { SafeAreaView, ScrollView, Platform, View } from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useRecoilValue } from 'recoil'
import { AcessosModel, ClienteLogAcessoModel } from '@/domain/models'
import { GetAccessLog } from '@/domain/usecases'
import { useErrorHandler, useThemeClient, useTheme } from '@/presentation/hooks'
import { Box, Button, DatePicker, dateAdapterAtom, HStack, Loading, Text, TextInput } from '@/presentation/components'
import { styles } from './styles'

type AccessLogProps = {
  getAccessLog: GetAccessLog
}

const formatDate = (value: string | Date): string => {
  const d = new Date(value)
  return d.toLocaleString('pt-BR')
}

export const AccessLog: React.FC<AccessLogProps> = ({ getAccessLog }) => {
  const handleError = useErrorHandler()
  const { getThemeClient, getAccentColor } = useThemeClient()
  const theme = useTheme()
  const themeClient = getThemeClient()
  const dateAdapter = useRecoilValue(dateAdapterAtom)

  const [isLoading, setIsLoading] = useState(false)
  const [isPeriodoLoading, setIsPeriodoLoading] = useState(false)
  const [data, setData] = useState<AcessosModel | null>(null)
  const [periodoList, setPeriodoList] = useState<ClienteLogAcessoModel[]>([])
  const [clienteFilter, setClienteFilter] = useState('')

  const [dataIni, setDataIni] = useState<Date | undefined>(dateAdapter.monthAgo(1))
  const [dataFim, setDataFim] = useState<Date | undefined>(dateAdapter.today())

  const load = useCallback((): void => {
    setIsLoading(true)
    getAccessLog
      .getAll()
      .then(setData)
      .catch(handleError)
      .finally(() => { setIsLoading(false) })
  }, [])

  useFocusEffect(useCallback(() => { load() }, []))

  const handleBuscar = (): void => {
    if (!dataIni || !dataFim) return
    setIsPeriodoLoading(true)
    getAccessLog
      .getByPeriodo({
        dataIni: dateAdapter.format(dataIni, 'YYYY-MM-DD'),
        dataFim: dateAdapter.format(dataFim, 'YYYY-MM-DD')
      })
      .then(setPeriodoList)
      .catch(handleError)
      .finally(() => { setIsPeriodoLoading(false) })
  }

  const handlePrint = (): void => {
    if (Platform.OS === 'web') window.print()
  }

  const filteredClientes = (data?.clientes ?? []).filter(
    (c) =>
      clienteFilter === '' ||
      c.nome.toLowerCase().includes(clienteFilter.toLowerCase()) ||
      c.codigo.toString().includes(clienteFilter)
  )

  const kpiCards = [
    { label: 'Hoje', value: data?.hoje ?? 0 },
    { label: 'Ontem', value: data?.ontem ?? 0 },
    { label: 'Mês Atual', value: data?.mes ?? 0 },
    { label: 'Mês Anterior', value: data?.anterior ?? 0 }
  ]

  const displayList = periodoList.length > 0 ? periodoList : (data?.ultimos ?? [])
  const tableTitle = periodoList.length > 0 ? `Acessos no Período (${periodoList.length})` : 'Últimos 30 acessos'

  return (
    <SafeAreaView style={styles.container}>
      <Box style={styles.container}>
        <View style={[styles.header, { backgroundColor: getAccentColor() }]}>
          <Text variant="subtitle1" style={styles.headerText}>
            Log de Acessos
          </Text>
          {Platform.OS === 'web' && (
            <Button
              variant='text'
              title='🖨 Imprimir'
              style={styles.printButton}
              color={theme.colors.white}
              onPress={handlePrint}
            />
          )}
        </View>

        {isLoading
          ? <Loading text='Carregando acessos...' />
          : (
            <ScrollView contentContainerStyle={styles.scrollContent}>
              <View style={styles.kpiRow}>
                {kpiCards.map((card) => (
                  <View key={card.label} style={[styles.kpiCard, { borderTopColor: themeClient.mainColor }]}>
                    <Text style={[styles.kpiLabel, { color: '#666' }]}>{card.label}</Text>
                    <Text style={[styles.kpiValue, { color: getAccentColor() }]}>{card.value}</Text>
                  </View>
                ))}
              </View>
              <View style={styles.filterCard}>
                <Text style={[styles.sectionTitle, { color: themeClient.mainColor }]}>
                  Acessos por Período
                </Text>
                <HStack style={styles.filterRow} items='center'>
                  <Box style={styles.dateInput}>
                    <DatePicker
                      label='Data Inicial'
                      value={dataIni}
                      onChange={(d) => setDataIni(d ?? dateAdapter.monthAgo(1))}
                      dateAdapter={dateAdapter}
                      backgroundColor='#fff'
                      textColor='#000'
                    />
                  </Box>
                  <Text style={styles.filterSep}>até</Text>
                  <Box style={styles.dateInput}>
                    <DatePicker
                      label='Data Final'
                      value={dataFim}
                      onChange={(d) => setDataFim(d ?? dateAdapter.today())}
                      dateAdapter={dateAdapter}
                      backgroundColor='#fff'
                      textColor='#000'
                    />
                  </Box>
                  <Button title='BUSCAR' color={themeClient.mainColor} onPress={handleBuscar} style={styles.buscarBtn} />
                </HStack>
              </View>
              <View style={styles.mainRow}>
                <View style={styles.tableCard}>
                  <Text style={[styles.sectionTitle, { color: themeClient.mainColor }]}>{tableTitle}</Text>
                  {isPeriodoLoading
                    ? <Loading text='Buscando...' />
                    : (
                      <View style={styles.tableWrapper}>
                        <View style={[styles.tableHeader, { backgroundColor: themeClient.mainColor }]}>
                          <Text style={[styles.colCodigo, styles.tableHeaderText]}>Código</Text>
                          <Text style={[styles.colCliente, styles.tableHeaderText]}>Cliente</Text>
                          <Text style={[styles.colData, styles.tableHeaderText]}>Data / Hora</Text>
                        </View>
                        {displayList.map((item, idx) => (
                          <View key={idx} style={[styles.tableRow, { backgroundColor: idx % 2 === 0 ? '#fff' : '#f6f6f9' }]}>
                            <Text style={[styles.colCodigo, styles.tableCell]}>{item.codigo}</Text>
                            <Text style={[styles.colCliente, styles.tableCell]}>{item.nome}</Text>
                            <Text style={[styles.colData, styles.tableCell]}>{formatDate(item.dataHora)}</Text>
                          </View>
                        ))}
                        {displayList.length === 0 && (
                          <Text style={styles.emptyText}>Nenhum acesso encontrado.</Text>
                        )}
                      </View>
                    )}
                </View>
                <View style={styles.sideCard}>
                  <Text style={[styles.sectionTitle, { color: themeClient.mainColor }]}>
                    Acessos por Cliente
                  </Text>
                  <TextInput
                    label='Buscar cliente'
                    value={clienteFilter}
                    onChangeText={setClienteFilter}
                    variant='outlined'
                  />
                  <ScrollView style={styles.sideScroll} showsVerticalScrollIndicator={false}>
                    {filteredClientes.map((c, idx) => (
                      <View key={idx} style={[styles.clientCard, { borderLeftColor: themeClient.mainColor }]}>
                        <HStack justify='space-between' items='center'>
                          <Text style={styles.clientName} numberOfLines={1}>{c.codigo} – {c.nome}</Text>
                          <View style={[styles.badge, { backgroundColor: themeClient.mainColor }]}>
                            <Text style={styles.badgeText}>{c.contador}</Text>
                          </View>
                        </HStack>
                        <Text style={styles.clientSub}>Último: {formatDate(c.dataHora)}</Text>
                      </View>
                    ))}
                    {filteredClientes.length === 0 && (
                      <Text style={styles.emptyText}>Nenhum cliente encontrado.</Text>
                    )}
                  </ScrollView>
                </View>

              </View>
            </ScrollView>
          )}
      </Box>
    </SafeAreaView>
  )
}


